import { DayOfWeek } from '../../worker-preferences'
import {
  CompanyFactKey,
  CompanyFactSchemaVersion,
  ConflictStrategy,
  type FactKeyConfig,
} from '../core'

/** @deprecated Kept only so existing BILLING_DETAILS_INVOICE_CYCLE_END fact rows still parse. */
export enum InvoiceCycleEndOption {
  WEEKLY_SUNDAY = 'WEEKLY_SUNDAY',
  WEEKLY_MONDAY = 'WEEKLY_MONDAY',
  WEEKLY_TUESDAY = 'WEEKLY_TUESDAY',
  WEEKLY_WEDNESDAY = 'WEEKLY_WEDNESDAY',
  WEEKLY_THURSDAY = 'WEEKLY_THURSDAY',
  WEEKLY_FRIDAY = 'WEEKLY_FRIDAY',
  WEEKLY_SATURDAY = 'WEEKLY_SATURDAY',
  DAILY = 'DAILY',
}

export enum InvoiceFrequencyOption {
  WEEKLY = 'WEEKLY',
  BIWEEKLY = 'BIWEEKLY',
}

export enum NetTermsOption {
  NET_15 = 'NET_15',
  NET_30 = 'NET_30',
  NET_45 = 'NET_45',
  NET_60 = 'NET_60',
  OTHER = 'OTHER',
}

export enum InvoiceDeliveryMethodOption {
  VIA_EMAIL = 'VIA_EMAIL',
  VIA_PORTAL = 'VIA_PORTAL',
}

/** How often workers are paid for BILLING_DETAILS_PAY_SCHEDULE. */
export enum PayScheduleFrequency {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
}

/** What triggers the overtime multiplier for BILLING_DETAILS_OVERTIME_PAY. */
export enum OvertimePayTrigger {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
}

/** The overtime pay multiplier for BILLING_DETAILS_OVERTIME_PAY. */
export enum OvertimePayMultiplier {
  X1 = '1x',
  X1_5 = '1.5x',
  X2 = '2x',
}

export const BILLING_DETAILS_KEY_CONFIG: Partial<
  Record<CompanyFactKey, FactKeyConfig>
> = {
  [CompanyFactKey.BILLING_DETAILS_INVOICE_CYCLE_END]: {
    schemaVersion: CompanyFactSchemaVersion.SINGLE_SELECT_V1,
    displayName: 'Invoice Cycle End',
    options: Object.values(InvoiceCycleEndOption),
  },
  [CompanyFactKey.BILLING_DETAILS_BILLING_EMAIL]: {
    schemaVersion: CompanyFactSchemaVersion.EMAIL_V1,
    displayName: 'Billing Email',
    conflictStrategy: ConflictStrategy.ALLOW_MULTIPLE,
  },
  [CompanyFactKey.BILLING_DETAILS_INVOICE_FREQUENCY]: {
    schemaVersion: CompanyFactSchemaVersion.SINGLE_SELECT_V1,
    displayName: 'Invoice Frequency',
    options: Object.values(InvoiceFrequencyOption),
  },
  [CompanyFactKey.BILLING_DETAILS_WORK_WEEK_END]: {
    schemaVersion: CompanyFactSchemaVersion.SINGLE_SELECT_V1,
    displayName: 'Work Week End',
    options: Object.values(DayOfWeek),
  },
  [CompanyFactKey.BILLING_DETAILS_NET_TERMS]: {
    schemaVersion: CompanyFactSchemaVersion.SINGLE_SELECT_WITH_NOTES_V1,
    displayName: 'Net Terms',
    options: Object.values(NetTermsOption),
  },
  [CompanyFactKey.BILLING_DETAILS_LATE_FEE_POLICY]: {
    schemaVersion: CompanyFactSchemaVersion.TEXT_V3,
    displayName: 'Late Fee Policy',
    conflictStrategy: ConflictStrategy.APPEND,
  },
  [CompanyFactKey.BILLING_DETAILS_PAY_SCHEDULE]: {
    schemaVersion: CompanyFactSchemaVersion.SINGLE_SELECT_WITH_NOTES_V1,
    displayName: 'Worker Pay Schedule',
    options: Object.values(PayScheduleFrequency),
  },
  [CompanyFactKey.BILLING_DETAILS_OVERTIME_PAY]: {
    schemaVersion: CompanyFactSchemaVersion.OVERTIME_PAY_V1,
    displayName: 'Overtime Pay',
    conflictStrategy: ConflictStrategy.ALLOW_MULTIPLE,
  },
  [CompanyFactKey.BILLING_DETAILS_INVOICE_DELIVERY_METHOD]: {
    schemaVersion: CompanyFactSchemaVersion.SINGLE_SELECT_WITH_NOTES_V1,
    displayName: 'Preferred Invoicing Process',
    options: Object.values(InvoiceDeliveryMethodOption),
    purposeTooltip:
      'How the customer wants invoices delivered. Use notes for the portal URL or per-portal instructions when delivery is via portal.',
  },
  [CompanyFactKey.BILLING_DETAILS_INVOICE_CC_EMAILS]: {
    schemaVersion: CompanyFactSchemaVersion.EMAIL_V1,
    displayName: 'Contact Emails for Invoices',
    conflictStrategy: ConflictStrategy.ALLOW_MULTIPLE,
  },
  [CompanyFactKey.BILLING_DETAILS_INVOICE_REQUIREMENTS]: {
    schemaVersion: CompanyFactSchemaVersion.TEXT_V3,
    displayName: 'Invoice Requirements',
    conflictStrategy: ConflictStrategy.APPEND,
  },
  [CompanyFactKey.BILLING_DETAILS_NOTES]: {
    schemaVersion: CompanyFactSchemaVersion.TEXT_V3,
    displayName: 'Notes',
    conflictStrategy: ConflictStrategy.APPEND,
  },
}
